import React from "react"
import PropTypes from "prop-types"
import { Button, Input, Badge, Alert } from "reactstrap"

import { useTestFormLogic } from "../Hooks/useTestFormLogic"
import ButtonLoader from "../../../components/Common/ButtonLoader"
import InputMask from "react-input-mask"

const TestEvaluationForm = ({
    idActivity,
    evaluationLogic,
}) => {
    const {
        isLoading,
        withLoading,
        formReady,
        activeTestEvent,
        allMembers,
        evaluationMembers,
        toggleExcludeMember,
        excludedMemberIds
    } = evaluationLogic

    const {
        testDrafts,
        handleResultChange,
        saveTests,
        dirtyCount: testsDirtyCount
    } = useTestFormLogic({
        idActivity,
        activeEvent: activeTestEvent,
        members: allMembers,
        excludedIds: excludedMemberIds,
        withLoading
    })

    // ─── LOADING GATE ───────────────────────────────────────────────
    if (!formReady) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="text-muted mb-0 font-size-14">Preparando teste...</p>
            </div>
        )
    }

    if (!activeTestEvent) {
        return (
            <Alert color="info" className="mb-4 d-flex align-items-center" fade={false}>
                <i className="mdi mdi-information-outline fs-4 me-3"></i>
                <div>
                    <h5 className="alert-heading font-size-14 mb-1">Ciclo de Testes indisponível</h5>
                    <p className="mb-0 small">Não há provas ou testes de performance agendados para este período.</p>
                </div>
            </Alert>
        )
    }
    // ─── FIM DO LOADING GATE ─────────────────────────────────────────
    const safeRender = (val) => {
        if (!val) return '';
        if (Array.isArray(val)) return val.map(v => typeof v === 'object' ? (v.label || v.description || v.id || '') : v).join(', ');
        if (typeof val === 'object') return val.label || val.name || val.description || val.value || val.id || '[Objeto]';
        return String(val);
    };

    return (
        <>
            <div className="mb-4 bg-light p-2 p-md-3 rounded border">
                <div className="d-flex align-items-center gap-3">
                    <div className="flex-shrink-0 bg-white rounded-circle border shadow-sm d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                        <i className={`mdi ${(['fixed_time', 'distance'].includes(activeTestEvent?.testType)) ? 'mdi-timer-sand' : 'mdi-run-fast'} fs-3 text-primary`}></i>
                    </div>
                    <div>
                        <h6 className="mb-1 fw-bold">{activeTestEvent.name}</h6>
                        <div className="small text-muted">
                            <strong>Prova: </strong>
                            {(['fixed_time', 'distance'].includes(activeTestEvent?.testType))
                                ? `Tempo Fixo (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Distância`
                                : `Distância Fixa (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Tempo`
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className="members-list">
                <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h6 className="mb-0 fw-bold">Alunos para Teste</h6>
                    <Badge color="dark" pill>{evaluationMembers.length}</Badge>
                </div>

                {evaluationMembers.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light border-dashed">
                        <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 mb-3 d-block"></i>
                        <p className="text-muted mb-0 font-size-13">Nenhum aluno apto encontrado.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {evaluationMembers.map(member => {
                            const sId = String(member.id)
                            const testDraft = testDrafts[sId] || { result: '', notes: '' }

                            return (
                                <div key={sId} className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-3 bg-white rounded border shadow-sm mb-2">
                                    <div className="d-flex align-items-center gap-3 w-100 w-md-auto">
                                        <div className="flex-shrink-0">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '48px', height: '48px' }}>
                                                    <i className="mdi mdi-account fs-3" style={{ color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <div className="fw-bold text-dark">{safeRender(member.name)}</div>
                                            {member.memberNumber && <span className="text-muted small">#{safeRender(member.memberNumber)}</span>}
                                        </div>
                                            <div className="d-flex gap-2 align-items-center mt-1">
                                                {member.status && (
                                                    <Badge color={
                                                        member.status === 'active' ? 'success' :
                                                            member.status === 'suspended' ? 'warning' : 'secondary'
                                                    } className="border px-2 text-capitalize">
                                                        {member.status === 'active' ? 'Ativo' : safeRender(member.status)}
                                                    </Badge>
                                                )}
                                                {member.tag && <Badge color="light" className="text-muted border d-none d-md-inline-block">{safeRender(member.tag)}</Badge>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column flex-md-row align-items-center gap-2 gap-md-3 w-100 w-md-auto ms-auto">

                                        <div className="w-100 w-md-auto" style={{ minWidth: '160px' }}>
                                            <div className="d-md-none small text-muted text-uppercase fw-bold mb-1 text-center">
                                                Inserir Resultado
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {['fixed_time', 'distance'].includes(activeTestEvent?.testType) ? (
                                                    <Input
                                                        type="number"
                                                        placeholder="Metros"
                                                        className="bg-light border-0 fw-bold text-center"
                                                        style={{ height: '38px', minWidth: '150px' }}
                                                        value={testDraft.result}
                                                        onChange={(e) => handleResultChange(sId, e.target.value)}
                                                        disabled={!activeTestEvent}
                                                    />
                                                ) : (
                                                    <InputMask
                                                        mask="99:99:99:99"
                                                        value={testDraft.result}
                                                        onChange={(e) => handleResultChange(sId, e.target.value)}
                                                        disabled={!activeTestEvent}
                                                    >
                                                        {(inputProps) => (
                                                            <Input
                                                                {...inputProps}
                                                                placeholder="HH:MM:SS:CC"
                                                                className="bg-light border-0 fw-bold text-center"
                                                                style={{ height: '38px', minWidth: '150px' }}
                                                            />
                                                        )}
                                                    </InputMask>
                                                )}
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2 w-100 w-md-auto justify-content-center">
                                            <Button
                                                outline
                                                color="danger"
                                                className="d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0 border-0"
                                                style={{ height: '38px', minWidth: '42px' }}
                                                onClick={() => toggleExcludeMember(sId)}
                                                title="Remover da lista"
                                            >
                                                <i className="mdi mdi-close-circle-outline fs-4" />
                                                <span className="ms-2 d-md-none fw-bold">Remover</span>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {excludedMemberIds.size > 0 && (
                    <div className="mt-4">
                        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                            <h6 className="small fw-bold text-muted text-uppercase mb-0">
                                <i className="mdi mdi-eye-off-outline me-1"></i>
                                Alunos Ocultos ({excludedMemberIds.size})
                            </h6>
                            <Button
                                color="link"
                                size="sm"
                                className="p-0 text-decoration-none small"
                                onClick={() => {
                                    excludedMemberIds.forEach(id => toggleExcludeMember(id))
                                }}
                            >
                                Restaurar Todos
                            </Button>
                        </div>

                        <div className="d-flex flex-column gap-2 bg-soft-light p-2 rounded border border-dashed">
                            {allMembers
                                .filter(c => excludedMemberIds.has(String(c.id)))
                                .map(member => (
                                    <div key={member.id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded border-bottom shadow-sm" style={{ opacity: 0.8 }}>
                                        <div className="d-flex align-items-center gap-2">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '24px', height: '24px' }}>
                                                    <i className="mdi mdi-account" style={{ fontSize: '14px', color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                            <span className="small fw-medium text-muted">{member.name}</span>
                                        </div>
                                        <Button
                                            color="primary"
                                            outline
                                            size="xs"
                                            className="py-0 px-2"
                                            style={{ fontSize: '0.65rem', height: '20px' }}
                                            onClick={() => toggleExcludeMember(member.id)}
                                        >
                                            Restaurar
                                        </Button>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
                    <ButtonLoader
                        color="primary"
                        onClick={saveTests}
                        loading={isLoading("saveTests")}
                        disabled={isLoading("saveTests") || testsDirtyCount === 0 || !activeTestEvent}
                        className="px-4 shadow-sm"
                    >
                        Salvar
                    </ButtonLoader>
                </div>
            </div>
        </>
    )
}

TestEvaluationForm.propTypes = {
    idActivity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    evaluationLogic: PropTypes.object
}

export default TestEvaluationForm
